import React from 'react';
import ShippingOption from './ShippingOption';
import type { ItemShippingType } from '@modules/ecommerce/context/ItemShippingContext';
import { useLoaderData } from 'react-router';
import { makeShippingOption } from '@modules/ecommerce/context/ItemShippingContext';
import type { loader } from '~/routes/_app.product.$urlSegment';
import type { ShippingMethodType } from '../../context/VariantShippingMethodsContext';

interface Props {
    shippingMethods: ShippingMethodType[];
    shipping: ItemShippingType | null;
    onChange: (shipping: ItemShippingType) => void;
}

export interface FreeThresholdData {
    FreeShippingThresholdMet: boolean;
    FreeShippingThreshold?: {
        Amount: number;
        ShortFormat: string;
    };
}

const DeliverySelect = ({ shippingMethods, shipping, onChange }: Props) => {
    const { shoppingCartSummary } = useLoaderData<typeof loader>();
    const selectedID = shipping && shipping.type === 'delivery' && shipping.ID;

    const cart = shoppingCartSummary as FreeThresholdData;

    const formatShippingCost = (
        shippingCostText: string,
        metFreeThreshold: boolean,
    ) => {
        const [price, unit] = shippingCostText.split('/');
        let shippingCost: string | JSX.Element = price ?? '';
        if (unit) {
            shippingCost = <span>{`${price}/${unit}`}</span>;
        }
        if (metFreeThreshold) {
            shippingCost = (
                <span>
                    <span className="line-through">{shippingCost}</span> Free
                </span>
            );
        }
        return shippingCost;
    };

    return (
        <div>
            <h3 className="mb-3 text-sm font-semibold tracking-normal">
                Delivery
            </h3>
            {shippingMethods.map((m) => {
                const hasMetFreeShippingThreshold = Boolean(
                    m.FreeThresholdEnabled &&
                    cart.FreeShippingThreshold &&
                    cart.FreeShippingThresholdMet,
                );
                const isDisabled = m.ShippingCost === null;

                return (
                    <ShippingOption
                        key={'shipping-method-' + m.ID}
                        selected={m.ID === selectedID}
                        title={
                            <div>
                                {m.Title}
                                {m.ShippingCost &&
                                    !m.QuotedSeparately &&
                                    m.ShippingCost.Amount > 0 && (
                                        <>
                                            {' '}
                                            -{' '}
                                            <span className="wrap-break-word">
                                                {formatShippingCost(
                                                    m.ShippingCostText ?? '',
                                                    hasMetFreeShippingThreshold,
                                                )}
                                            </span>
                                        </>
                                    )}
                            </div>
                        }
                        subTitle={
                            <>
                                {m.DeliveryEstimateText}
                                {m.QuotedSeparately && (
                                    <>
                                        {m.DeliveryEstimateText && <br />}
                                        Supplier will contact you after purchase
                                        to quote shipping fee
                                    </>
                                )}
                                {m.FreeThresholdEnabled &&
                                    cart.FreeShippingThreshold &&
                                    !cart.FreeShippingThresholdMet && (
                                        <>
                                            {m.DeliveryEstimateText && <br />}
                                            Free Shipping for orders over{' '}
                                            {
                                                cart.FreeShippingThreshold
                                                    .ShortFormat
                                            }
                                        </>
                                    )}
                            </>
                        }
                        disabled={isDisabled}
                        onClick={() => {
                            const option = makeShippingOption(
                                'delivery',
                                m.ID,
                                m.ShippingProvider,
                            );
                            if (!isDisabled) {
                                onChange(option);
                            }
                        }}
                    />
                );
            })}
        </div>
    );
};

export default DeliverySelect;
